import { useMemo } from "react";
import type { GeocodingFeature } from "@tinyclub/shared/models";
import { cn } from "@/components/ui/cn";

interface ClubOption {
  label: string;
  prefillName: string | null;
}

interface OnboardingStartClubStepProps {
  onOptionSelected: (prefillName: string | null) => Promise<void>;
  onSkip: () => void;
  userLocationObject?: GeocodingFeature | null;
}

export function OnboardingStartClubStep({
  onOptionSelected,
  onSkip,
  userLocationObject,
}: OnboardingStartClubStepProps) {
  const neighborhoodClubName = useMemo(() => {
    if (!userLocationObject?.properties?.context) return "My neighborhood";

    const context = userLocationObject.properties.context;

    const streetName = context.street?.name;
    if (streetName) {
      const cleaned = streetName
        .replace(
          /\b(Street|Avenue|Road|Drive|Lane|Court|Place|Way|Boulevard)\b/gi,
          ""
        )
        .trim();
      return `${cleaned} neighbors`;
    }

    const neighborhoodName = context.neighborhood?.name;
    if (neighborhoodName) return `${neighborhoodName} neighbors`;

    return "My neighborhood";
  }, [userLocationObject]);

  const clubOptions: ClubOption[] = [
    { label: "I have a club in mind...", prefillName: null },
    { label: neighborhoodClubName, prefillName: neighborhoodClubName },
    { label: "Parents of littles", prefillName: "Parents of littles" },
    { label: "Adventure crew", prefillName: "Adventure crew" },
    { label: "Pickleball pals", prefillName: "Pickleball pals" },
    { label: "Listening club", prefillName: "Listening club" },
  ];

  return (
    <div className="flex flex-col h-full">
      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto autohide-scrollbar px-8 pt-4 pb-8">
        <div className="flex flex-col gap-6">
          <h1 className="text-h1 text-text-primary">
            What kind of club do you want to start?
          </h1>

          <div className="flex flex-col gap-3">
            {/* First option: outlined */}
            <button
              onClick={() => onOptionSelected(clubOptions[0].prefillName)}
              className="w-full py-3.5 rounded-full border border-border-tertiary bg-background text-body-1-medium text-text-primary font-medium hover:border-border-secondary transition-colors"
            >
              {clubOptions[0].label}
            </button>

            {/* Template options */}
            <div className="flex flex-col gap-2">
              {clubOptions.slice(1).map((option, index) => (
                <button
                  key={index}
                  onClick={() => onOptionSelected(option.prefillName)}
                  className={cn(
                    "w-full py-3.5 rounded-full text-body-1-medium text-text-primary font-medium transition-colors",
                    "bg-surface-1 hover:bg-surface-2"
                  )}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Footer with Skip button */}
      <div className="flex-shrink-0 px-8 pb-6 pt-4">
        <button
          onClick={onSkip}
          className="w-full py-3.5 rounded-full border border-border-tertiary text-body-1-medium text-text-primary font-medium hover:border-border-secondary transition-colors"
        >
          Skip
        </button>
      </div>
    </div>
  );
}
